// billing.js - Enhanced billing component with modern UI and improved UX
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Link, useNavigate } from "react-router-dom";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getFacilitiesURL, getFacilityUnits } from "../../../utils/urls";
import Layout from "../component/layout";
import { filterObjectsByAllProperties } from "../../../utils/filterSearch";
import {
  getFacilityInvoices,
  getAllFacilitiesInvoices,
} from "./utils/invoiceFetcher";

// Import parent tab components
import FacilityInvoicesParentTab from "./tabs/FacilityInvoicesParentTab";
import SettingsParentTab from "./tabs/SettingsParentTab";

const BillingDashboard = () => {
  const [facilities, setFacilities] = useState([]);
  const [facilityUnits, setFacilityUnits] = useState({});
  const [facilityInvoices, setFacilityInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeParentTab, setActiveParentTab] = useState("overview");
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  // Enhanced stats calculation
  const dashboardStats = useMemo(() => {
    const totalInvoices = facilityInvoices.length;
    const totalAmount = facilityInvoices.reduce(
      (sum, inv) => sum + (inv.amount || 0),
      0,
    );
    const paidInvoices = facilityInvoices.filter(
      (inv) => inv.status === "paid",
    ).length;
    const pendingInvoices = facilityInvoices.filter(
      (inv) => inv.status === "pending",
    ).length;
    const overdueInvoices = facilityInvoices.filter(
      (inv) => inv.status === "overdue",
    ).length;
    const paidAmount = facilityInvoices
      .filter((inv) => inv.status === "paid")
      .reduce((sum, inv) => sum + (inv.amount || 0), 0);
    const pendingAmount = facilityInvoices
      .filter((inv) => inv.status === "pending")
      .reduce((sum, inv) => sum + (inv.amount || 0), 0);

    return {
      totalInvoices,
      totalAmount,
      paidInvoices,
      pendingInvoices,
      overdueInvoices,
      paidAmount,
      pendingAmount,
      collectionRate: totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0,
    };
  }, [facilityInvoices]);

  // Facility stats
  const facilityStats = useMemo(() => {
    return facilities.map((facility) => {
      const facilityInvoicesList = facilityInvoices.filter(
        (inv) => inv.facilityId === facility._id,
      );
      const totalAmount = facilityInvoicesList.reduce(
        (sum, inv) => sum + (inv.amount || 0),
        0,
      );
      const unitsCount = facilityUnits[facility._id]?.length || 0;

      return {
        ...facility,
        invoiceCount: facilityInvoicesList.length,
        totalAmount,
        unitsCount,
        status: totalAmount > 0 ? "active" : "inactive",
      };
    });
  }, [facilities, facilityInvoices, facilityUnits]);

  const getFacilityUnitsData = async (facilities) => {
    const unitsData = {};
    try {
      const unitsPromises = facilities.map(async (facility) => {
        try {
          const response = await makeRequest2(
            `${getFacilityUnits}/${facility._id}`,
            "GET",
          );
          if (response.success) {
            unitsData[facility._id] = response.data;
          } else {
            unitsData[facility._id] = [];
          }
        } catch (error) {
          console.error(
            `Error fetching units for facility ${facility._id}:`,
            error,
          );
          unitsData[facility._id] = [];
        }
      });
      await Promise.all(unitsPromises);
      return unitsData;
    } catch (error) {
      console.error("Error fetching facility units:", error);
      return {};
    }
  };

  const getAllInvoices = async (facilities) => {
    try {
      return await getAllFacilitiesInvoices(facilities, {
        showToast: false,
      });
    } catch (error) {
      console.error("Error fetching all invoices:", error);
      return [];
    }
  };

  const refreshFacilityInvoices = async (facilityId, newInvoices = null) => {
    try {
      let result;
      if (newInvoices) {
        result = { success: true, invoices: newInvoices };
      } else {
        result = await getFacilityInvoices(facilityId);
      }

      if (result.success) {
        setFacilityInvoices((prevInvoices) => {
          const otherInvoices = prevInvoices.filter(
            (inv) => inv.facilityId !== facilityId,
          );
          const facility = facilities.find((f) => f._id === facilityId);
          const invoicesWithFacilityName = result.invoices.map((invoice) => ({
            ...invoice,
            facilityName: facility?.name || "Unknown Facility",
          }));
          return [...otherInvoices, ...invoicesWithFacilityName];
        });
        return result;
      }
      return result;
    } catch (error) {
      console.error(
        `Error refreshing invoices for facility ${facilityId}:`,
        error,
      );
      toastify("Error refreshing invoices: " + error.message, "error");
      return { success: false, error: error.message };
    }
  };

  const getFacilities = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const response = await makeRequest2(getFacilitiesURL, "GET", {});
      if (response.success) {
        setFacilities(response.data);
        const unitsData = await getFacilityUnitsData(response.data);
        setFacilityUnits(unitsData);
        const invoicesData = await getAllInvoices(response.data);
        setFacilityInvoices(invoicesData);
        toastify("Data refreshed successfully!", "success");
      }
    } catch (error) {
      toastify(error.message, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleInputSearch = async (value) => {
    try {
      setSearchTerm(value);
      if (value === "") {
        getFacilities(false);
      } else {
        let results = [];
        switch (activeParentTab) {
          case "facility-invoices":
            results = await filterObjectsByAllProperties(
              facilityInvoices,
              value,
            );
            setFacilityInvoices(results);
            break;
          default:
            break;
        }
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  const handleViewInvoice = (invoice) => {
    const facility = facilities.find((f) => f._id === invoice.facilityId);
    navigate(`/core/billing/invoice/${invoice._id}`, {
      state: { invoice, facility },
    });
  };

  const handleGenerateInvoice = async (facilityId = null) => {
    try {
      const endpoint = facilityId
        ? `/api/core/billing/facility/${facilityId}/generate-invoice`
        : "/api/core/billing/generate-invoice";

      const body = facilityId ? {} : { generateForAll: true };
      const message = facilityId
        ? "Generating invoice for facility..."
        : "Generating new invoices...";

      toastify(message, "info");

      const response = await makeRequest2(endpoint, "POST", body);

      if (response.success) {
        const successMessage = facilityId
          ? "Invoice generated successfully for facility!"
          : "Invoices generated successfully!";

        toastify(successMessage, "success");

        if (facilityId) {
          await refreshFacilityInvoices(facilityId);
        } else {
          await getFacilities(false);
        }
      } else {
        toastify("Failed to generate invoices: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error generating invoices: " + error.message, "error");
    }
  };

  const getFacilityInvoiceSummary = async (facilityId) => {
    try {
      const result = await getFacilityInvoices(facilityId, {
        showToast: false,
      });
      if (result.success) {
        return result.summary;
      }
      return null;
    } catch (error) {
      console.error(`Error getting summary for facility ${facilityId}:`, error);
      return null;
    }
  };

  useEffect(() => {
    getFacilities();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusSeverity = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "overdue":
        return "danger";
      default:
        return "info";
    }
  };

  // Enhanced Overview Dashboard
  const renderOverviewDashboard = () => (
    <div className="row g-4">
      {/* Key Metrics Cards */}
      <div className="col-12">
        <div className="row g-3">
          <div className="col-xl-3 col-md-6">
            <div className="card bg-primary text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Invoices</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="60px" height="32px" />
                      ) : (
                        dashboardStats.totalInvoices.toLocaleString()
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-receipt fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-success text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Revenue</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="80px" height="32px" />
                      ) : (
                        formatCurrency(dashboardStats.totalAmount)
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-currency-dollar fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-info text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Collection Rate</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="50px" height="32px" />
                      ) : (
                        `${dashboardStats.collectionRate.toFixed(1)}%`
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-trending-up fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-warning text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Active Facilities</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="40px" height="32px" />
                      ) : (
                        facilities.length
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-building fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Breakdown Cards */}
      <div className="col-12">
        <div className="row g-3">
          <div className="col-md-4">
            <div className="card border-success border-2 h-100">
              <div className="card-body text-center">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <div
                    className="bg-success bg-opacity-10 rounded-circle p-3"
                    style={{ width: "60px", height: "60px" }}
                  >
                    <i className="ti ti-check fs-3 text-success"></i>
                  </div>
                </div>
                <h5 className="text-success mb-2">Paid Invoices</h5>
                <h3 className="mb-2">
                  {loading ? (
                    <Skeleton width="40px" height="28px" />
                  ) : (
                    dashboardStats.paidInvoices
                  )}
                </h3>
                <p className="text-muted mb-0">
                  {loading ? (
                    <Skeleton width="80px" height="16px" />
                  ) : (
                    formatCurrency(dashboardStats.paidAmount)
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-warning border-2 h-100">
              <div className="card-body text-center">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <div
                    className="bg-warning bg-opacity-10 rounded-circle p-3"
                    style={{ width: "60px", height: "60px" }}
                  >
                    <i className="ti ti-clock fs-3 text-warning"></i>
                  </div>
                </div>
                <h5 className="text-warning mb-2">Pending Invoices</h5>
                <h3 className="mb-2">
                  {loading ? (
                    <Skeleton width="40px" height="28px" />
                  ) : (
                    dashboardStats.pendingInvoices
                  )}
                </h3>
                <p className="text-muted mb-0">
                  {loading ? (
                    <Skeleton width="80px" height="16px" />
                  ) : (
                    formatCurrency(dashboardStats.pendingAmount)
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card border-danger border-2 h-100">
              <div className="card-body text-center">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <div
                    className="bg-danger bg-opacity-10 rounded-circle p-3"
                    style={{ width: "60px", height: "60px" }}
                  >
                    <i className="ti ti-alert-circle fs-3 text-danger"></i>
                  </div>
                </div>
                <h5 className="text-danger mb-2">Overdue Invoices</h5>
                <h3 className="mb-2">
                  {loading ? (
                    <Skeleton width="40px" height="28px" />
                  ) : (
                    dashboardStats.overdueInvoices
                  )}
                </h3>
                <p className="text-muted mb-0">
                  Requires Attention
                  {dashboardStats.overdueInvoices > 0 && (
                    <Badge
                      value={dashboardStats.overdueInvoices}
                      severity="danger"
                      className="ms-2"
                    />
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Facilities Overview */}
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="ti ti-building me-2"></i>Facilities Overview
              </h5>
              <Button
                icon="ti ti-refresh"
                className="p-button-text p-button-sm"
                onClick={() => getFacilities(false)}
                loading={refreshing}
                tooltip="Refresh Data"
              />
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="row g-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="col-md-4">
                    <div className="card border">
                      <div className="card-body">
                        <Skeleton width="100%" height="120px" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="row g-3">
                {facilityStats.map((facility) => (
                  <div key={facility._id} className="col-xl-4 col-md-6">
                    <div
                      className={`card border-start border-4 h-100 ${
                        facility.status === "active"
                          ? "border-success"
                          : "border-secondary"
                      }`}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-start justify-content-between mb-3">
                          <div>
                            <h6 className="fw-bold mb-1">{facility.name}</h6>
                            <Badge
                              value={facility.status}
                              severity={
                                facility.status === "active"
                                  ? "success"
                                  : "secondary"
                              }
                              className="text-capitalize"
                            />
                          </div>
                          <Button
                            icon="ti ti-plus"
                            className="p-button-sm p-button-text"
                            onClick={() => handleGenerateInvoice(facility._id)}
                            tooltip="Generate Invoice"
                          />
                        </div>

                        <div className="row g-2 text-center">
                          <div className="col-4">
                            <div className="bg-light p-2 rounded">
                              <div className="fw-bold text-primary">
                                {facility.invoiceCount}
                              </div>
                              <small className="text-muted">Invoices</small>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="bg-light p-2 rounded">
                              <div className="fw-bold text-success">
                                {facility.unitsCount}
                              </div>
                              <small className="text-muted">Units</small>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="bg-light p-2 rounded">
                              <div className="fw-bold text-info small">
                                {formatCurrency(facility.totalAmount).replace(
                                  "KES",
                                  "K",
                                )}
                              </div>
                              <small className="text-muted">Revenue</small>
                            </div>
                          </div>
                        </div>

                        {facility.location && (
                          <div className="mt-2">
                            <small className="text-muted">
                              <i className="ti ti-map-pin me-1"></i>
                              {facility.location}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderParentTabContent = () => {
    switch (activeParentTab) {
      case "overview":
        return renderOverviewDashboard();
      case "facility-invoices":
        return (
          <FacilityInvoicesParentTab
            facilityInvoices={facilityInvoices}
            facilities={facilities}
            facilityUnits={facilityUnits}
            loading={loading}
            onViewInvoice={handleViewInvoice}
            onGenerateInvoice={handleGenerateInvoice}
            onRefreshFacilityInvoices={refreshFacilityInvoices}
            getFacilityInvoiceSummary={getFacilityInvoiceSummary}
            searchTerm={searchTerm}
            onSearch={handleInputSearch}
          />
        );
      case "settings":
        return <SettingsParentTab facilities={facilities} />;
      default:
        return null;
    }
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case "overview":
        return "ti ti-dashboard";
      case "facility-invoices":
        return "ti ti-receipt";
      case "settings":
        return "ti ti-settings";
      default:
        return "ti ti-circle";
    }
  };

  const getTabLabel = (tab) => {
    switch (tab) {
      case "overview":
        return "Overview";
      case "facility-invoices":
        return "Invoices";
      case "settings":
        return "Settings";
      default:
        return "Tab";
    }
  };

  const showSearchAndActions =
    activeParentTab !== "settings" && activeParentTab !== "overview";

  return (
    <Layout>
      {/* Subtle Header */}
      <div className="page-header bg-light border-bottom">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-8">
              <ul className="breadcrumb mb-2">
                <li className="breadcrumb-item">
                  <Link to="/core/dashboard" className="text-muted">
                    <i className="ti ti-home me-1"></i>Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active text-dark">
                  Billing Management
                </li>
              </ul>
              <div className="page-header-title">
                <h2 className="mb-1 text-dark fw-semibold">
                  <i className="ti ti-calculator me-2 text-primary"></i>Billing
                  Management
                </h2>
                <p className="mb-0 text-muted">
                  Manage facility invoices and billing settings
                </p>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  label="Generate All Invoices"
                  icon="ti ti-plus"
                  className="p-button-outlined"
                  onClick={() => handleGenerateInvoice()}
                  loading={loading}
                />
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-secondary"
                  onClick={() => getFacilities(false)}
                  loading={refreshing}
                  tooltip="Refresh Data"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          {/* Enhanced Navigation Tabs */}
          <div className="card border-0 shadow-sm">
            <div className="card-body py-3">
              <ul className="nav nav-pills nav-justified" role="tablist">
                {["overview", "facility-invoices", "settings"].map((tab) => (
                  <li className="nav-item" key={tab}>
                    <button
                      className={`nav-link d-flex align-items-center justify-content-center ${
                        activeParentTab === tab ? "active" : ""
                      }`}
                      onClick={() => setActiveParentTab(tab)}
                      type="button"
                    >
                      <i className={`${getTabIcon(tab)} me-2 fs-5`}></i>
                      <span className="fw-semibold">{getTabLabel(tab)}</span>
                      {tab === "facility-invoices" && !loading && (
                        <Badge
                          value={dashboardStats.totalInvoices}
                          severity="secondary"
                          className="ms-2"
                        />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Enhanced Search and Filters */}
          {showSearchAndActions && (
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row align-items-center g-3">
                  <div className="col-md-4">
                    <div className="position-relative">
                      <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      <InputText
                        value={searchTerm}
                        onChange={(e) => handleInputSearch(e.target.value)}
                        placeholder="Search invoices, facilities..."
                        className="form-control ps-5"
                        style={{ paddingLeft: "2.5rem" }}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <Dropdown
                      value={selectedFacility}
                      onChange={(e) => setSelectedFacility(e.value)}
                      options={[
                        { label: "All Facilities", value: null },
                        ...facilities.map((f) => ({
                          label: f.name,
                          value: f._id,
                        })),
                      ]}
                      placeholder="Filter by Facility"
                      className="w-100"
                    />
                  </div>
                  <div className="col-md-3">
                    <Calendar
                      value={dateRange}
                      onChange={(e) => setDateRange(e.value)}
                      selectionMode="range"
                      placeholder="Select Date Range"
                      className="w-100"
                      showIcon
                    />
                  </div>
                  <div className="col-md-2 text-end">
                    <div className="d-flex gap-1">
                      <Button
                        icon="ti ti-file-export"
                        className="p-button-outlined p-button-sm"
                        tooltip="Export"
                        onClick={() => toastify("Exporting data...", "info")}
                      />
                      <Button
                        icon="ti ti-printer"
                        className="p-button-outlined p-button-sm"
                        tooltip="Print"
                        onClick={() => toastify("Preparing print...", "info")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="tab-content">
            {loading && activeParentTab === "overview" ? (
              <div className="text-center py-5">
                <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                <p className="mt-3 text-muted">Loading billing data...</p>
              </div>
            ) : (
              <div className="mt-3">{renderParentTabContent()}</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BillingDashboard;
